import type { HeelRaiseLength, HeelRaiseType, SubcalOffType } from './prescriptionState';

/**
 * Rearfoot heel-raise + subcalcaneal-offloading availability rules.
 *
 * EXTRACTED, NOT AUTHORED. Every rule below was previously an inline `.filter`
 * in RearfootSection's JSX. It is lifted here verbatim so that two callers can
 * ask the same question and get the same answer:
 *
 *   1. the section, which renders the narrowed option list (as before), and
 *   2. `collectUnavailableValues`, which decides whether a value already in
 *      form state is still offered by the product being ordered.
 *
 * (2) is the reason this file exists. A copied prescription can carry a value
 * the target product does not offer, and the select then renders BLANK while
 * the illegal value stays live in state and prints on the lab sheet. The
 * validator cannot see an inline filter inside a component that, in the case
 * that matters, is never even mounted (sections are collapsed by default and
 * CollapsibleCard unmounts its children).
 *
 * Follows the `rearfootWedgeRules.ts` precedent: pure, dependency-free,
 * unit-tested, imported by both the form and the checker so they cannot drift.
 */

/** All heel-raise type values, in display order. */
export const HEEL_RAISE_TYPE_VALUES: readonly HeelRaiseType[] = [
  'none',
  'intrinsic',
  'extrinsic',
];

/** All heel-raise length values, in display order. */
export const HEEL_RAISE_LENGTH_VALUES: readonly HeelRaiseLength[] = ['short', 'long'];

/** All subcalcaneal-offloading type values, in display order. */
export const SUBCAL_OFFLOADING_TYPE_VALUES: readonly SubcalOffType[] = [
  'none',
  'heel_offloading',
  'heel_cutout',
];

/**
 * R38 heel-raise availability by shell:
 *   - Carbon / fibreglass: extrinsic only (the raise is glued under the shell).
 *   - EVA / TPU: intrinsic only (the raise is built into the shell).
 *   - PA12 / polypropylene (and any other shell): both.
 *   - Sandal: extrinsic is hidden regardless of shell.
 */
export function heelRaiseTypesFor(
  subtype: string | null | undefined,
  isSandal: boolean,
): readonly HeelRaiseType[] {
  const hideIntrinsic = subtype === 'carbon_insole' || subtype === 'fibreglass_insole';
  const hideExtrinsic = subtype === 'eva_insole' || subtype === 'tpu_insole';
  return HEEL_RAISE_TYPE_VALUES.filter(
    (t) =>
      !(hideIntrinsic && t === 'intrinsic') &&
      !(hideExtrinsic && t === 'extrinsic') &&
      !(isSandal && t === 'extrinsic'),
  );
}

/**
 * An intrinsic heel raise on an insole is only manufacturable over the
 * calcaneus, so its length is locked to `short`. On a sandal, and for an
 * extrinsic raise, both lengths are available.
 *
 * Note this is a DEPENDENT rule: its answer changes with the sibling `type`
 * field, so a caller must pass the type currently held in state, not the type
 * it wishes were there.
 */
export function heelRaiseLengthsFor(
  type: HeelRaiseType | undefined,
  isSandal: boolean,
): readonly HeelRaiseLength[] {
  if (type === 'intrinsic' && !isSandal) {
    return HEEL_RAISE_LENGTH_VALUES.filter((l) => l === 'short');
  }
  return HEEL_RAISE_LENGTH_VALUES;
}

/** R37: sandals hide the focal heel-cutout option. */
export function subcalOffloadingTypesFor(isSandal: boolean): readonly SubcalOffType[] {
  return isSandal
    ? SUBCAL_OFFLOADING_TYPE_VALUES.filter((t) => t !== 'heel_cutout')
    : SUBCAL_OFFLOADING_TYPE_VALUES;
}
